import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Volume2 } from "lucide-react"

import {
  Badge,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@f2e/ui/components/base"

import { FetchStatus } from "@/hooks/useCodeerApi"
import type {
  Text2SpeechConfig,
  Text2SpeechVoiceLanguageOption,
  Text2SpeechModelOption,
  Text2SpeechVoiceOption,
} from "@/types/text2speechTool"

import { InvocationInstructionField } from "./InvocationInstructionField"
import type { UnifiedTool } from "./type"

const DEFAULT_TEXT2SPEECH_MODEL_ID = "eleven_multilingual_v2"
const DEFAULT_TEXT2SPEECH_OUTPUT_FORMAT = "mp3_44100_128"
const AUTO_LANGUAGE_SELECT_VALUE = "__auto__"
const ALL_GENDERS_SELECT_VALUE = "__all__"

const LANGUAGE_METADATA_HINTS: Record<string, string[]> = {
  zh: ["chinese", "mandarin", "taiwan", "taiwanese", "cantonese", "hong kong"],
  ja: ["japanese", "japan", "tokyo"],
  en: ["english", "american", "british", "australian", "canadian"],
}

const getVoiceSearchTarget = (voice: Text2SpeechVoiceOption) =>
  [
    voice.name,
    voice.description,
    voice.category,
    ...Object.values(voice.labels || {}),
    ...voice.verifiedLanguages.flatMap((language) => [
      language.languageCode,
      language.locale,
      language.accent,
      language.modelId,
    ]),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase()

const getVoiceGender = (voice: Text2SpeechVoiceOption) => {
  const gender = (voice.labels?.gender || "").toLowerCase()
  if (gender.includes("female")) return "female"
  if (gender.includes("male")) return "male"
  return undefined
}

const normalizeLanguageTag = (value?: string) =>
  value?.trim().toLowerCase().replace(/_/g, "-")

const getLanguageRoot = (value?: string) =>
  normalizeLanguageTag(value)?.split("-")[0]

const matchesRequestedLanguage = (
  preview: Text2SpeechVoiceLanguageOption,
  selectedLanguageCode?: string
) => {
  const normalizedSelectedLanguageCode =
    normalizeLanguageTag(selectedLanguageCode)
  if (!normalizedSelectedLanguageCode) return false

  const selectedLanguageRoot = getLanguageRoot(normalizedSelectedLanguageCode)
  const previewLanguageCode = normalizeLanguageTag(preview.languageCode)
  const previewLocale = normalizeLanguageTag(preview.locale)

  return (
    previewLanguageCode === normalizedSelectedLanguageCode ||
    previewLocale === normalizedSelectedLanguageCode ||
    getLanguageRoot(previewLanguageCode) === selectedLanguageRoot ||
    getLanguageRoot(previewLocale) === selectedLanguageRoot
  )
}

const scorePreviewCandidate = (
  preview: Text2SpeechVoiceLanguageOption,
  selectedLanguageCode?: string,
  selectedModelId?: string
) => {
  let score = 0
  const normalizedSelectedLanguageCode =
    normalizeLanguageTag(selectedLanguageCode)
  const normalizedSelectedModelId = normalizeLanguageTag(selectedModelId)
  const previewLanguageCode = normalizeLanguageTag(preview.languageCode)
  const previewLocale = normalizeLanguageTag(preview.locale)
  const previewModelId = normalizeLanguageTag(preview.modelId)

  if (normalizedSelectedLanguageCode) {
    const selectedLanguageRoot = getLanguageRoot(normalizedSelectedLanguageCode)

    if (
      previewLanguageCode === normalizedSelectedLanguageCode ||
      previewLocale === normalizedSelectedLanguageCode
    ) {
      score += 100
    } else if (
      getLanguageRoot(previewLanguageCode) === selectedLanguageRoot ||
      getLanguageRoot(previewLocale) === selectedLanguageRoot
    ) {
      score += 80
    }
  }

  if (
    normalizedSelectedModelId &&
    previewModelId === normalizedSelectedModelId
  ) {
    score += 20
  }

  return score
}

export const getPreferredVoicePreviewUrl = (
  voice?: Text2SpeechVoiceOption,
  selectedLanguageCode?: string,
  selectedModelId?: string
) => {
  if (!voice) return undefined

  const previewCandidates = voice.verifiedLanguages.filter(
    (preview) => !!preview.previewUrl
  )

  if (previewCandidates.length === 0) {
    return voice.previewUrl
  }

  const modelMatchedLanguagePreviews = previewCandidates
    .filter(
      (preview) =>
        matchesRequestedLanguage(preview, selectedLanguageCode) &&
        normalizeLanguageTag(preview.modelId) ===
          normalizeLanguageTag(selectedModelId)
    )
    .sort(
      (left, right) =>
        scorePreviewCandidate(right, selectedLanguageCode, selectedModelId) -
        scorePreviewCandidate(left, selectedLanguageCode, selectedModelId)
    )

  if (modelMatchedLanguagePreviews.length > 0) {
    return modelMatchedLanguagePreviews[0].previewUrl
  }

  const languageMatchedPreviews = previewCandidates
    .filter((preview) =>
      matchesRequestedLanguage(preview, selectedLanguageCode)
    )
    .sort(
      (left, right) =>
        scorePreviewCandidate(right, selectedLanguageCode, selectedModelId) -
        scorePreviewCandidate(left, selectedLanguageCode, selectedModelId)
    )

  if (languageMatchedPreviews.length > 0) {
    return languageMatchedPreviews[0].previewUrl
  }

  if (selectedLanguageCode) {
    return undefined
  }

  const modelMatchedPreviews = previewCandidates.filter(
    (preview) =>
      normalizeLanguageTag(preview.modelId) ===
      normalizeLanguageTag(selectedModelId)
  )
  if (modelMatchedPreviews.length > 0) {
    return modelMatchedPreviews[0].previewUrl
  }

  return voice.previewUrl || previewCandidates[0].previewUrl
}

export const getPreferredVoicePreviewMetadata = (
  voice?: Text2SpeechVoiceOption,
  selectedLanguageCode?: string,
  selectedModelId?: string
) => {
  if (!voice) return undefined

  const previewUrl = getPreferredVoicePreviewUrl(
    voice,
    selectedLanguageCode,
    selectedModelId
  )
  if (!previewUrl) return undefined

  const matchedPreview = voice.verifiedLanguages.find(
    (preview) => preview.previewUrl === previewUrl
  )

  return {
    previewUrl,
    sampleLabel:
      matchedPreview?.locale || matchedPreview?.languageCode || undefined,
    isLanguageMatched:
      !selectedLanguageCode ||
      (matchedPreview
        ? matchesRequestedLanguage(matchedPreview, selectedLanguageCode)
        : false),
  }
}

const scoreVoiceForLanguage = (
  voice: Text2SpeechVoiceOption,
  languageCode?: string
) => {
  if (!languageCode) return 0

  const normalizedLanguageCode = languageCode.toLowerCase()
  const languageRoot = normalizedLanguageCode.split("-")[0]

  let score = 0
  for (const verifiedLanguage of voice.verifiedLanguages) {
    const verifiedCode = verifiedLanguage.languageCode?.toLowerCase()
    const verifiedLocale = verifiedLanguage.locale?.toLowerCase()

    if (verifiedCode === normalizedLanguageCode) score += 8
    else if (verifiedCode?.startsWith(`${languageRoot}-`)) score += 7
    else if (verifiedCode === languageRoot) score += 6

    if (verifiedLocale === normalizedLanguageCode) score += 6
    else if (verifiedLocale?.startsWith(`${languageRoot}-`)) score += 5
  }

  const metadataTarget = getVoiceSearchTarget(voice)
  const hints = LANGUAGE_METADATA_HINTS[languageRoot] || []
  if (hints.some((hint) => metadataTarget.includes(hint))) score += 2

  return score
}

export const getOrderedVoiceOptions = (
  voices: Text2SpeechVoiceOption[],
  languageCode?: string
) =>
  [...voices].sort((left, right) => {
    const scoreDiff =
      scoreVoiceForLanguage(right, languageCode) -
      scoreVoiceForLanguage(left, languageCode)
    if (scoreDiff !== 0) return scoreDiff
    return left.name.localeCompare(right.name)
  })

export const getNextVoiceIdForSelection = ({
  currentVoiceId,
  voiceOptions,
  previousFilterSelectionKey,
  nextFilterSelectionKey,
}: {
  currentVoiceId?: string
  voiceOptions: Text2SpeechVoiceOption[]
  previousFilterSelectionKey: string | null
  nextFilterSelectionKey: string
}) => {
  const firstVoiceId = voiceOptions[0]?.voiceId
  if (!firstVoiceId) {
    return {
      nextVoiceId: currentVoiceId,
      nextFilterSelectionKey: previousFilterSelectionKey,
    }
  }

  const currentSelectionVisible = voiceOptions.some(
    (voice) => voice.voiceId === currentVoiceId
  )

  if (previousFilterSelectionKey === null) {
    return {
      nextVoiceId:
        !currentVoiceId || !currentSelectionVisible
          ? firstVoiceId
          : currentVoiceId,
      nextFilterSelectionKey: nextFilterSelectionKey,
    }
  }

  if (previousFilterSelectionKey !== nextFilterSelectionKey) {
    return {
      nextVoiceId: firstVoiceId,
      nextFilterSelectionKey: nextFilterSelectionKey,
    }
  }

  if (!currentSelectionVisible) {
    return {
      nextVoiceId: firstVoiceId,
      nextFilterSelectionKey: previousFilterSelectionKey,
    }
  }

  return {
    nextVoiceId: currentVoiceId,
    nextFilterSelectionKey: previousFilterSelectionKey,
  }
}

export interface Text2SpeechToolSectionProps {
  tool: UnifiedTool
  disabled: boolean
  models: Text2SpeechModelOption[]
  voices: Text2SpeechVoiceOption[]
  modelsStatus: FetchStatus
  voicesStatus: FetchStatus
  modelsError?: unknown
  voicesError?: unknown
  modelsMessage?: string
  voicesMessage?: string
  onUpdateTool: (toolId: string, updates: Partial<UnifiedTool>) => void
  onInvalidSelectionChange: (toolId: string, hasInvalid: boolean) => void
}

const buildDefaultConfig = (): Text2SpeechConfig => ({
  provider: "elevenlabs",
  voiceId: "",
  modelId: DEFAULT_TEXT2SPEECH_MODEL_ID,
  outputFormat: DEFAULT_TEXT2SPEECH_OUTPUT_FORMAT,
})

const normalizeConfig = (tool: UnifiedTool): Text2SpeechConfig => ({
  ...buildDefaultConfig(),
  ...tool.text2speech,
})

export const Text2SpeechToolSection = ({
  tool,
  disabled,
  models,
  voices,
  modelsStatus,
  voicesStatus,
  modelsError,
  voicesError,
  modelsMessage,
  voicesMessage,
  onUpdateTool,
  onInvalidSelectionChange,
}: Text2SpeechToolSectionProps) => {
  const { t } = useTranslation()
  const [voiceGender, setVoiceGender] = useState(ALL_GENDERS_SELECT_VALUE)

  const config = normalizeConfig(tool)
  const selectedModel = models.find((model) => model.modelId === config.modelId)
  const selectedVoice = voices.find((voice) => voice.voiceId === config.voiceId)
  const selectedVoicePreview = useMemo(
    () =>
      getPreferredVoicePreviewMetadata(
        selectedVoice,
        config.languageCode,
        config.modelId
      ),
    [config.languageCode, config.modelId, selectedVoice]
  )

  const updateText2Speech = useCallback(
    (updates: Partial<Text2SpeechConfig>) => {
      onUpdateTool(tool.id, {
        text2speech: {
          ...config,
          ...updates,
        },
      })
    },
    [config, onUpdateTool, tool.id]
  )

  const blurFocusedElement = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur()
    }
  }

  useEffect(() => {
    if (tool.text2speech) return
    onUpdateTool(tool.id, { text2speech: buildDefaultConfig() })
  }, [tool.id, tool.text2speech, onUpdateTool])

  useEffect(() => {
    const hasInvalidSelection = !config.modelId.trim() || !config.voiceId.trim()
    onInvalidSelectionChange(tool.id, hasInvalidSelection)
  }, [config.modelId, config.voiceId, onInvalidSelectionChange, tool.id])

  useEffect(() => {
    const availableLanguages = selectedModel?.languages ?? []
    if (!config.languageCode || availableLanguages.length === 0) return
    const languageStillAvailable = availableLanguages.some(
      (language) => language.languageCode === config.languageCode
    )
    if (!languageStillAvailable) {
      onUpdateTool(tool.id, {
        text2speech: {
          ...config,
          languageCode: undefined,
        },
      })
    }
  }, [config, onUpdateTool, selectedModel, tool.id])

  const modelLanguages = selectedModel?.languages ?? []
  const selectedLanguageName =
    modelLanguages.find(
      (language) => language.languageCode === config.languageCode
    )?.name || config.languageCode

  const baseFilteredVoices = voices.filter((voice) => {
    const matchesGender =
      voiceGender === ALL_GENDERS_SELECT_VALUE ||
      getVoiceGender(voice) === voiceGender

    return matchesGender
  })

  const languageMatchedVoices = config.languageCode
    ? baseFilteredVoices.filter(
        (voice) => scoreVoiceForLanguage(voice, config.languageCode) > 0
      )
    : []

  const hasExplicitLanguageFilter = Boolean(config.languageCode)
  const voiceOptions = getOrderedVoiceOptions(
    baseFilteredVoices,
    config.languageCode
  )
  const voiceFilterSelectionKey = `${config.modelId}|${
    config.languageCode || AUTO_LANGUAGE_SELECT_VALUE
  }|${voiceGender}|${hasExplicitLanguageFilter ? "language-priority" : "all"}`
  const lastVoiceFilterSelectionKeyRef = React.useRef<string | null>(null)

  const voiceMetaBadges = [
    selectedVoice?.category,
    selectedVoice?.labels?.gender,
    selectedVoice?.labels?.accent,
  ].filter(Boolean) as string[]
  const isFilteringVoices = voiceGender !== ALL_GENDERS_SELECT_VALUE
  const voiceLabelHint = config.languageCode
    ? languageMatchedVoices.length > 0
      ? t(
          "workspace.agent.basicForm.unifiedToolSelector.text2SpeechVoiceLabelWithLanguageMatchHint",
          {
            count: languageMatchedVoices.length,
            language: selectedLanguageName || config.languageCode,
          }
        )
      : baseFilteredVoices.length > 0
        ? t(
            "workspace.agent.basicForm.unifiedToolSelector.text2SpeechVoiceLabelWithLanguageFallbackHint",
            {
              language: selectedLanguageName || config.languageCode,
            }
          )
        : t(
            "workspace.agent.basicForm.unifiedToolSelector.text2SpeechVoiceLabelWithLanguageEmptyHint",
            {
              language: selectedLanguageName || config.languageCode,
            }
          )
    : null

  useEffect(() => {
    const { nextVoiceId, nextFilterSelectionKey } = getNextVoiceIdForSelection({
      currentVoiceId: config.voiceId,
      voiceOptions,
      previousFilterSelectionKey: lastVoiceFilterSelectionKeyRef.current,
      nextFilterSelectionKey: voiceFilterSelectionKey,
    })

    lastVoiceFilterSelectionKeyRef.current = nextFilterSelectionKey
    if (nextVoiceId !== config.voiceId) {
      updateText2Speech({ voiceId: nextVoiceId || "" })
    }
  }, [config.voiceId, updateText2Speech, voiceFilterSelectionKey, voiceOptions])

  return (
    <div className="space-y-4">
      <InvocationInstructionField
        id={`text2speech-invocation-${tool.id}`}
        placeholder={t(
          "workspace.agent.basicForm.unifiedToolSelector.text2SpeechInvocationInstructionPlaceholder"
        )}
        value={tool.invocationInstruction}
        onChange={(value) =>
          onUpdateTool(tool.id, {
            invocationInstruction: value,
          })
        }
        disabled={disabled}
      />

      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <Label
            htmlFor={`text2speech-model-${tool.id}`}
            className="text-sm font-medium"
          >
            {t(
              "workspace.agent.basicForm.unifiedToolSelector.text2SpeechModelLabel"
            )}
          </Label>
          <Select
            value={config.modelId}
            onValueChange={(value) => updateText2Speech({ modelId: value })}
            disabled={disabled || models.length === 0}
          >
            <SelectTrigger id={`text2speech-model-${tool.id}`}>
              <SelectValue
                placeholder={t(
                  "workspace.agent.basicForm.unifiedToolSelector.text2SpeechModelPlaceholder"
                )}
              />
            </SelectTrigger>
            <SelectContent>
              {models.map((model) => (
                <SelectItem key={model.modelId} value={model.modelId}>
                  {model.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {modelsStatus === FetchStatus.Loading && models.length === 0 ? (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t(
                "workspace.agent.basicForm.unifiedToolSelector.text2SpeechModelLoading"
              )}
            </p>
          ) : modelsError ? (
            <p className="text-xs text-red-500 dark:text-red-900">
              {t(
                "workspace.agent.basicForm.unifiedToolSelector.text2SpeechModelLoadFailed"
              )}
            </p>
          ) : models.length === 0 ? (
            <p className="text-xs text-amber-600 dark:text-amber-400">
              {modelsMessage ||
                t(
                  "workspace.agent.basicForm.unifiedToolSelector.text2SpeechModelUnavailable"
                )}
            </p>
          ) : !config.modelId ? (
            <p className="text-xs text-red-500 dark:text-red-900">
              {t(
                "workspace.agent.basicForm.unifiedToolSelector.text2SpeechModelRequired"
              )}
            </p>
          ) : (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t(
                "workspace.agent.basicForm.unifiedToolSelector.text2SpeechModelHelp"
              )}
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label
            htmlFor={`text2speech-language-${tool.id}`}
            className="text-sm font-medium"
          >
            {t(
              "workspace.agent.basicForm.unifiedToolSelector.text2SpeechLanguageLabel"
            )}
          </Label>
          <Select
            value={config.languageCode || AUTO_LANGUAGE_SELECT_VALUE}
            onValueChange={(value) =>
              updateText2Speech({
                languageCode:
                  value === AUTO_LANGUAGE_SELECT_VALUE ? undefined : value,
              })
            }
            disabled={disabled || modelLanguages.length === 0}
          >
            <SelectTrigger id={`text2speech-language-${tool.id}`}>
              <SelectValue
                placeholder={t(
                  "workspace.agent.basicForm.unifiedToolSelector.text2SpeechLanguagePlaceholder"
                )}
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={AUTO_LANGUAGE_SELECT_VALUE}>
                {t(
                  "workspace.agent.basicForm.unifiedToolSelector.text2SpeechLanguageAuto"
                )}
              </SelectItem>
              {modelLanguages.map((language) => (
                <SelectItem
                  key={language.languageCode}
                  value={language.languageCode}
                >
                  {language.name || language.languageCode}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {modelLanguages.length > 0
              ? t(
                  "workspace.agent.basicForm.unifiedToolSelector.text2SpeechLanguageHelp"
                )
              : t(
                  "workspace.agent.basicForm.unifiedToolSelector.text2SpeechLanguageUnavailable"
                )}
          </p>
        </div>
      </div>

      <div className="space-y-2">
        <Label
          htmlFor={`text2speech-voice-gender-${tool.id}`}
          className="text-sm font-medium"
        >
          {t(
            "workspace.agent.basicForm.unifiedToolSelector.text2SpeechVoiceGenderLabel"
          )}
        </Label>
        <Select
          value={voiceGender}
          onValueChange={(value) => setVoiceGender(value)}
          disabled={disabled || voices.length === 0}
        >
          <SelectTrigger id={`text2speech-voice-gender-${tool.id}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_GENDERS_SELECT_VALUE}>
              {t(
                "workspace.agent.basicForm.unifiedToolSelector.text2SpeechVoiceGenderAll"
              )}
            </SelectItem>
            <SelectItem value="female">
              {t(
                "workspace.agent.basicForm.unifiedToolSelector.text2SpeechVoiceGenderFemale"
              )}
            </SelectItem>
            <SelectItem value="male">
              {t(
                "workspace.agent.basicForm.unifiedToolSelector.text2SpeechVoiceGenderMale"
              )}
            </SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {t(
            "workspace.agent.basicForm.unifiedToolSelector.text2SpeechVoiceGenderHelp"
          )}
        </p>
      </div>

      <div className="space-y-2">
        <Label
          htmlFor={`text2speech-voice-${tool.id}`}
          className="text-sm font-medium"
        >
          {voiceLabelHint
            ? `${t(
                "workspace.agent.basicForm.unifiedToolSelector.text2SpeechVoiceLabel"
              )} (${voiceLabelHint})`
            : t(
                "workspace.agent.basicForm.unifiedToolSelector.text2SpeechVoiceLabel"
              )}
        </Label>
        <Select
          value={config.voiceId}
          onValueChange={(value) => updateText2Speech({ voiceId: value })}
          onOpenChange={(open) => open && blurFocusedElement()}
          disabled={disabled || voices.length === 0}
        >
          <SelectTrigger id={`text2speech-voice-${tool.id}`}>
            <SelectValue
              placeholder={t(
                "workspace.agent.basicForm.unifiedToolSelector.text2SpeechVoicePlaceholder"
              )}
            />
          </SelectTrigger>
          <SelectContent>
            {voiceOptions.map((voice) => (
              <SelectItem key={voice.voiceId} value={voice.voiceId}>
                {voice.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {voicesStatus === FetchStatus.Loading && voices.length === 0 ? (
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {t(
              "workspace.agent.basicForm.unifiedToolSelector.text2SpeechVoiceLoading"
            )}
          </p>
        ) : voicesError ? (
          <p className="text-xs text-red-500 dark:text-red-900">
            {t(
              "workspace.agent.basicForm.unifiedToolSelector.text2SpeechVoiceLoadFailed"
            )}
          </p>
        ) : voices.length === 0 ? (
          <p className="text-xs text-amber-600 dark:text-amber-400">
            {voicesMessage ||
              t(
                "workspace.agent.basicForm.unifiedToolSelector.text2SpeechVoiceUnavailable"
              )}
          </p>
        ) : !config.voiceId ? (
          <p className="text-xs text-red-500 dark:text-red-900">
            {t(
              "workspace.agent.basicForm.unifiedToolSelector.text2SpeechVoiceRequired"
            )}
          </p>
        ) : voiceOptions.length === 0 ? (
          <p className="text-xs text-red-500 dark:text-red-900">
            {t(
              "workspace.agent.basicForm.unifiedToolSelector.text2SpeechVoiceNoResults"
            )}
          </p>
        ) : isFilteringVoices ? (
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {t(
              "workspace.agent.basicForm.unifiedToolSelector.text2SpeechVoiceSearchResult",
              {
                count: voiceOptions.length,
                query: t(
                  "workspace.agent.basicForm.unifiedToolSelector.text2SpeechVoiceFilterFallbackQuery"
                ),
              }
            )}
          </p>
        ) : (
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {t(
              "workspace.agent.basicForm.unifiedToolSelector.text2SpeechVoiceHelp"
            )}
          </p>
        )}
      </div>

      <div className="rounded-xl border border-amber-200 bg-gradient-to-br from-amber-50 via-white to-orange-50 p-4 dark:border-amber-900/60 dark:from-amber-950/30 dark:via-background dark:to-orange-950/20">
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Volume2 className="h-4 w-4 text-amber-600 dark:text-amber-400" />
              <p className="text-sm font-medium">
                {selectedVoice?.name ||
                  t(
                    "workspace.agent.basicForm.unifiedToolSelector.text2SpeechPreviewTitle"
                  )}
              </p>
            </div>
            {voiceMetaBadges.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {voiceMetaBadges.map((value) => (
                  <Badge key={value} variant="secondary" className="text-xs">
                    {value}
                  </Badge>
                ))}
              </div>
            )}
            <p className="text-xs text-gray-600 dark:text-gray-300">
              {selectedVoice?.description ||
                t(
                  "workspace.agent.basicForm.unifiedToolSelector.text2SpeechPreviewHelp"
                )}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t(
                "workspace.agent.basicForm.unifiedToolSelector.text2SpeechPreviewLanguageNote"
              )}
            </p>
            {selectedVoicePreview?.sampleLabel ? (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t(
                  "workspace.agent.basicForm.unifiedToolSelector.text2SpeechPreviewSampleLabel",
                  { sample: selectedVoicePreview.sampleLabel }
                )}
              </p>
            ) : null}
            {config.languageCode &&
            selectedVoice &&
            !selectedVoicePreview?.previewUrl ? (
              <p className="text-xs text-amber-700 dark:text-amber-300">
                {t(
                  "workspace.agent.basicForm.unifiedToolSelector.text2SpeechPreviewLanguageUnavailable",
                  {
                    language: selectedLanguageName || config.languageCode,
                  }
                )}
              </p>
            ) : null}
          </div>
          {config.outputFormat ? (
            <Badge variant="outline" className="text-xs">
              {config.outputFormat}
            </Badge>
          ) : null}
        </div>

        <div className="mt-3">
          {selectedVoicePreview?.previewUrl ? (
            <audio
              key={selectedVoicePreview.previewUrl}
              controls
              preload="none"
              className="w-full"
            >
              <source src={selectedVoicePreview.previewUrl} />
            </audio>
          ) : (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t(
                "workspace.agent.basicForm.unifiedToolSelector.text2SpeechPreviewUnavailable"
              )}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
